import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { User, Patient, Encounter, Role } from '../types';
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, writeBatch, query } from 'firebase/firestore';
interface DataContextType {
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
  currentUser: User | null;
  login: (fullName: string, password?: string) => boolean;
  logout: () => void;
  // User Management
  addUser: (user: Omit<User, 'id'>) => void;
  updateUser: (id: string, user: Partial<User>) => void;
  deleteUser: (id: string) => void;
  // Patient Management
  addPatient: (patient: Omit<Patient, 'id' | 'bmcFileNumber' | 'registeredAt'>) => Patient;
  updatePatient: (id: string, data: Partial<Patient>) => void;
  deletePatient: (id: string) => void;
  // Encounter Management
  createEncounter: (patientId: string) => Encounter;
  updateEncounter: (id: string, data: Partial<Encounter>) => void;
  getPatientEncounters: (patientId: string) => Encounter[];
  getActiveEncounters: () => Encounter[];
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const STORAGE_KEY = 'hms_data_v1';
interface StorageData {
  users: User[];
  patients: Patient[];
  encounters: Encounter[];
}
const defaultAdmin: User = {
  id: 'admin-1',
  fullName: 'System Admin',
  password: 'AdminPassword123!',
  role: 'Administrator'
};
// Firestore collection names
const USERS_COL = 'users';
const PATIENTS_COL = 'patients';
const ENCOUNTERS_COL = 'encounters';
export const DataProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  // Track whether Firestore has delivered initial data
  const firestoreReady = useRef({
    users: false,
    patients: false,
    encounters: false
  });
  const allFirestoreReady = () => firestoreReady.current.users && firestoreReady.current.patients && firestoreReady.current.encounters;
  // Load from localStorage as initial fallback, then Firestore takes over
  useEffect(() => {
    // Load localStorage cache first for instant display
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: StorageData = JSON.parse(stored);
        setUsers(parsed.users?.length ? parsed.users : [defaultAdmin]);
        setPatients(parsed.patients || []);
        setEncounters(parsed.encounters || []);
      } catch (e) {
        console.error('Failed to parse stored data', e);
        setUsers([defaultAdmin]);
      }
    } else {
      setUsers([defaultAdmin]);
    }
    // Check session
    const session = sessionStorage.getItem('hms_session');
    if (session) {
      try {
        const user = JSON.parse(session);
        setCurrentUser(user);
      } catch (e) {
        // ignore
      }
    }
    setIsLoaded(true);
    // Set up Firestore real-time listeners
    const unsubUsers = onSnapshot(query(collection(db, USERS_COL)), snapshot => {
      if (!snapshot.empty) {
        const fireUsers = snapshot.docs.map(d => ({
          ...d.data(),
          id: d.id
        }) as User);
        setUsers(fireUsers);
      } else if (!firestoreReady.current.users) {
        // First load and empty — seed default admin to Firestore
        setDoc(doc(db, USERS_COL, defaultAdmin.id), defaultAdmin).catch(console.error);
        setUsers([defaultAdmin]);
      }
      firestoreReady.current.users = true;
    }, error => {
      console.error('Firestore users listener error:', error);
    });
    const unsubPatients = onSnapshot(query(collection(db, PATIENTS_COL)), snapshot => {
      const firePatients = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id
      }) as Patient);
      setPatients(firePatients);
      firestoreReady.current.patients = true;
    }, error => {
      console.error('Firestore patients listener error:', error);
    });
    const unsubEncounters = onSnapshot(query(collection(db, ENCOUNTERS_COL)), snapshot => {
      const fireEncounters = snapshot.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          labRequests: data.labRequests || [],
          radiologyRequests: data.radiologyRequests || [],
          prescriptions: data.prescriptions || []
        } as Encounter;
      });
      setEncounters(fireEncounters);
      firestoreReady.current.encounters = true;
    }, error => {
      console.error('Firestore encounters listener error:', error);
    });
    return () => {
      unsubUsers();
      unsubPatients();
      unsubEncounters();
    };
  }, []);
  // Save to localStorage as cache whenever data changes
  useEffect(() => {
    if (!isLoaded) return;
    const data: StorageData = {
      users,
      patients,
      encounters
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [users, patients, encounters, isLoaded]);
  const login = (fullName: string, password?: string) => {
    const user = users.find(u => u.fullName === fullName && u.password === password);
    if (user) {
      setCurrentUser(user);
      sessionStorage.setItem('hms_session', JSON.stringify(user));
      return true;
    }
    return false;
  };
  const logout = () => {
    setCurrentUser(null);
    sessionStorage.removeItem('hms_session');
  };
  const addUser = (user: Omit<User, 'id'>) => {
    const newUser: User = {
      ...user,
      id: `user-${Date.now()}`
    };
    // Write to Firestore — onSnapshot will update local state
    setDoc(doc(db, USERS_COL, newUser.id), newUser).catch(err => {
      console.error('Failed to add user to Firestore:', err);
      // Fallback: update local state directly
      setUsers(prev => [...prev, newUser]);
    });
  };
  const updateUser = (id: string, data: Partial<User>) => {
    // Optimistic local update
    setUsers(prev => prev.map(u => u.id === id ? {
      ...u,
      ...data
    } : u));
    updateDoc(doc(db, USERS_COL, id), data).catch(err => {
      console.error('Failed to update user in Firestore:', err);
    });
  };
  const deleteUser = (id: string) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    deleteDoc(doc(db, USERS_COL, id)).catch(err => {
      console.error('Failed to delete user from Firestore:', err);
    });
  };
  const generateBMCNumber = () => {
    const year = new Date().getFullYear();
    const count = patients.length + 1;
    return `BMC${year}/${count.toString().padStart(4, '0')}`;
  };
  const addPatient = (patientData: Omit<Patient, 'id' | 'bmcFileNumber' | 'registeredAt'>) => {
    const newPatient: Patient = {
      ...patientData,
      id: `pat-${Date.now()}`,
      bmcFileNumber: generateBMCNumber(),
      registeredAt: new Date().toISOString()
    };
    // Write to Firestore
    setDoc(doc(db, PATIENTS_COL, newPatient.id), newPatient).catch(err => {
      console.error('Failed to add patient to Firestore:', err);
      setPatients(prev => [...prev, newPatient]);
    });
    // Return immediately for the caller (optimistic)
    setPatients(prev => [...prev, newPatient]);
    return newPatient;
  };
  const updatePatient = (id: string, data: Partial<Patient>) => {
    setPatients(prev => prev.map(p => p.id === id ? {
      ...p,
      ...data
    } : p));
    updateDoc(doc(db, PATIENTS_COL, id), data).catch(err => {
      console.error('Failed to update patient in Firestore:', err);
    });
  };
  const deletePatient = (id: string) => {
    setPatients(prev => prev.filter(p => p.id !== id));
    setEncounters(prev => prev.filter(e => e.patientId !== id));
    // Delete from Firestore
    deleteDoc(doc(db, PATIENTS_COL, id)).catch(err => {
      console.error('Failed to delete patient from Firestore:', err);
    });
    // Also delete associated encounters from Firestore
    encounters.filter(e => e.patientId === id).forEach(e => {
      deleteDoc(doc(db, ENCOUNTERS_COL, e.id)).catch(console.error);
    });
  };
  const createEncounter = (patientId: string) => {
    const newEncounter: Encounter = {
      id: `enc-${Date.now()}`,
      patientId,
      startedAt: new Date().toISOString(),
      stage: 'NURSING',
      pendingLab: false,
      pendingRadiology: false,
      labRequests: [],
      radiologyRequests: [],
      prescriptions: []
    };
    // Optimistic local update
    setEncounters(prev => [...prev, newEncounter]);
    // Write to Firestore
    setDoc(doc(db, ENCOUNTERS_COL, newEncounter.id), newEncounter).catch(err => {
      console.error('Failed to create encounter in Firestore:', err);
    });
    return newEncounter;
  };
  const updateEncounter = (id: string, data: Partial<Encounter>) => {
    // Optimistic local update
    setEncounters(prev => prev.map(e => e.id === id ? {
      ...e,
      ...data
    } : e));
    // Write to Firestore
    updateDoc(doc(db, ENCOUNTERS_COL, id), data as Record<string, any>).catch(err => {
      console.error('Failed to update encounter in Firestore:', err);
    });
  };
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const getActiveEncounters = () => {
    return encounters.filter(e => e.stage !== 'COMPLETED');
  };
  if (!isLoaded) return null;
  return <DataContext.Provider value={{
    users,
    patients,
    encounters,
    currentUser,
    login,
    logout,
    addUser,
    updateUser,
    deleteUser,
    addPatient,
    updatePatient,
    deletePatient,
    createEncounter,
    updateEncounter,
    getPatientEncounters,
    getActiveEncounters
  }} data-id="element-1720">
      {children}
    </DataContext.Provider>;
};
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};